import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ResumoEventoDiaService } from 'src/services/resumo-evento.service';
import { ConfiguracaoEventoService } from 'src/services/configuracao-evento.service';
import * as moment from 'moment';
import { corFundoDiaMonitoramento } from '../classificacao-coleta';

const EVENTOS_TOTAL_IRREGULAR = [1, 3, 4, 5, 9];

@Component({
    selector: 'app-relatorio-resumo-evento',
    templateUrl: './relatorio-resumo-evento.component.html',
    styleUrls: ['./relatorio-resumo-evento.component.scss'],
    standalone: false
})
export class RelatorioResumoEventoComponent implements OnInit {
  data: any[] = [];
  columns: any[] = [];
  rangeDate: Date[] = [];
  loading: boolean = false;
  quebraPaginaPorEstacao: boolean = false;

  filtrosAplicados: any = {
    estacoes: [],
    grupos: [],
    clientes: [],
    sensores: []
  };

  dataGeracao: string = '';
  periodoFormatado: string = '';
  eventosCadastrados: any[] = [];

  private navigationState: any;

  constructor(
    private router: Router,
    private _resumoEventoService: ResumoEventoDiaService,
    private _configuracaoEventoService: ConfiguracaoEventoService
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.navigationState = navigation?.extras?.state || history.state;
  }

  ngOnInit(): void {
    this.dataGeracao = moment().format('DD/MM/YYYY [às] HH:mm');

    if (this.navigationState?.rangeDate) {
      this.rangeDate = this.navigationState.rangeDate;
      this.filtrosAplicados = {
        estacoes: this.navigationState.estacao_id || [],
        grupos: this.navigationState.grupo || [],
        clientes: this.navigationState.cliente || [],
        sensores: this.navigationState.sensor || []
      };
      this.carregarEventosCadastrados();
      this.carregarDados();
    } else {
      this.router.navigate(['/monitoramento-estacoes']);
    }
  }

  carregarEventosCadastrados(): void {
    this._configuracaoEventoService.index().subscribe({
      next: (res) => {
        this.eventosCadastrados = (res.data || res || []).filter((item: any) => item.ativo !== false);
      },
      error: (err) => {
        console.error('Erro ao carregar eventos cadastrados:', err);
      }
    });
  }

  carregarDados(): void {
    this.loading = true;

    const estacoes = this.filtrosAplicados.estacoes?.map(e => e.value) || [];
    const grupos = this.filtrosAplicados.grupos?.map(g => g.value) || [];
    const clientes = this.filtrosAplicados.clientes?.map(c => c.value) || [];
    const sensores = this.filtrosAplicados.sensores?.map(s => s.value) || [];

    const inicioData = moment(this.rangeDate[0]).format('YYYY-MM-DD');
    const fimData = moment(this.rangeDate[1]).format('YYYY-MM-DD');

    this.periodoFormatado = `${moment(this.rangeDate[0]).format('DD/MM/YYYY')} a ${moment(this.rangeDate[1]).format('DD/MM/YYYY')}`;

    this._resumoEventoService.filter(
      inicioData,
      fimData,
      estacoes,
      grupos,
      clientes,
      sensores,
      this.navigationState?.plu_filtro,
      this.navigationState?.flu_filtro,
      this.navigationState?.integrador_filtro?.map(i => i.id) || [],
      this.navigationState?.coletor_filtro?.map(c => c.id) || []
    ).subscribe(
      json => {
        this.processarDados(json);
        this.loading = false;
      },
      err => {
        console.error('Erro ao carregar dados:', err);
        this.loading = false;
      }
    );
  }

  processarDados(json: any[]): void {
    const weekdays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

    this.columns = [];
    this.data = [];

    let currentDate = new Date(this.rangeDate[0]);

    while (currentDate <= new Date(this.rangeDate[1])) {
      const dayOfWeek = weekdays[currentDate.getDay()];
      const formattedDate = moment(currentDate).format('DD/MM/YYYY');
      this.columns.push({
        field: `${formattedDate} ${dayOfWeek}`,
        header: `${dayOfWeek} ${formattedDate}`,
        data_resumo: moment(currentDate).format('YYYY-MM-DD'),
        diaSemana: dayOfWeek,
        dataFormatada: formattedDate
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (json) {
      json.forEach(item => {
        const eventos = this.processarEventos(item.eventos || [], item.configuracoes_evento || []);
        const monitoramento = this.processarMonitoramentos(item.monitoramentos || [], this.rangeDate);

        this.data.push({
          estacao_id: item.id,
          descricao: item.descricao,
          desativar_integracao: item.desativar_integracao === true || item.desativar_integracao === 1 || item.desativar_integracao === '1',
          configuracoes_evento: item.configuracoes_evento ?? [],
          eventos: eventos,
          monitoramento: this.removerDuplicados(monitoramento)
        });
      });
    }
  }

  processarEventos(eventos: any[], configuracoes: any[]): any[] {
    return eventos.map(evento => {
      let cfg = configuracoes.find(x => x.configuracao_evento_id == evento.tipo_evento_id);
      if (cfg) {
        if (EVENTOS_TOTAL_IRREGULAR.includes(Number(evento.tipo_evento_id))) {
          evento.total_computado = Number(evento.total_computado_irregular ?? 0);
        } else if (cfg.tipo_criterio_pelo_irregural_regular == 0) {
          evento.total_computado = evento.total_computado_irregular;
        } else if (cfg.tipo_criterio_pelo_irregural_regular == 1) {
          evento.total_computado = evento.total_computado_normal;
        }
      }
      evento.cor = evento.situaca_evento === 0 ? '#6c8f3f' : '#b0662f';
      return evento;
    }).sort((a, b) => a.tipo_evento_id - b.tipo_evento_id);
  }

  processarMonitoramentos(monitoramentos: any[], rangeDate: Date[]): any[] {
    const result: any[] = [];

    monitoramentos.forEach(mon => {
      const eventos = mon.eventos || [];
      const dataInicio = moment(mon.data_hora_monitoramento).startOf('day');
      const dataFim = mon.data_hora_ultima_situacao
        ? moment(mon.data_hora_ultima_situacao).startOf('day')
        : moment(rangeDate[1]).startOf('day');

      if (eventos.length > 0) {
        eventos.forEach(evento => {
          const tipoEventoId = evento.tipo_evento_id;
          let currentDate = dataInicio.clone();
          while (currentDate.isSameOrBefore(dataFim)) {
            if (currentDate.isSameOrAfter(moment(rangeDate[0])) && currentDate.isSameOrBefore(moment(rangeDate[1]))) {
              result.push({
                ...mon,
                tipo_evento_id: tipoEventoId,
                dataMonitoramento: currentDate.format('YYYY-MM-DD'),
                cor: this.determinarCorMonitoramento(tipoEventoId),
                descricao: this.getDescricaoTipoEvento(tipoEventoId)
              });
            }
            currentDate.add(1, 'day');
          }
        });
      } else {
        let currentDate = dataInicio.clone();
        while (currentDate.isSameOrBefore(dataFim)) {
          if (currentDate.isSameOrAfter(moment(rangeDate[0])) && currentDate.isSameOrBefore(moment(rangeDate[1]))) {
            result.push({
              ...mon,
              tipo_evento_id: null,
              dataMonitoramento: currentDate.format('YYYY-MM-DD'),
              cor: 'black',
              descricao: 'Monitoramento'
            });
          }
          currentDate.add(1, 'day');
        }
      }
    });

    return result;
  }

  determinarCorMonitoramento(tipoEventoId: number): string {
    switch (tipoEventoId) {
      case 7:
      case 8:
        return 'red';
      case 1:
      case 2:
        return 'orange';
      case 3:
      case 4:
      case 5:
        return 'blue';
      case 6:
        return 'gray';
      default:
        return 'black';
    }
  }

  removerDuplicados(event: any[]): any[] {
    let ids = new Set();
    return event.filter(item => {
      const isAberto = Number(item.status) === 0;
      if (!isAberto) {
        if (item.dataMonitoramento > moment(item.data_hora_ultima_situacao).format('YYYY-MM-DD') ||
            item.dataMonitoramento < moment(item.data_hora_monitoramento).format('YYYY-MM-DD')) {
          return false;
        }
      } else if (item.dataMonitoramento < moment(item.data_hora_monitoramento).format('YYYY-MM-DD')) {
        return false;
      }
      let chave = item.id + '_' + item.tipo_evento_id + '_' + item.dataMonitoramento;
      if (ids.has(chave)) {
        return false;
      }
      ids.add(chave);
      return true;
    });
  }

  getBackgroundColorForDay(rowData: any, dataResumo: string): string {
    return corFundoDiaMonitoramento(rowData, dataResumo, {
      fallback: '#e0e0e0',
      exigirAtivo: true,
    });
  }

  hasEventosNaData(rowData: any, dataResumo: string): boolean {
    return rowData.eventos?.some((row: any) => row.data_resumo === dataResumo);
  }

  hasMonitoramentosNaData(rowData: any, dataResumo: string): boolean {
    return rowData.monitoramento?.some((mon: any) => mon.dataMonitoramento === dataResumo);
  }

  getEventosNaData(rowData: any, dataResumo: string): any[] {
    return rowData.eventos?.filter((row: any) => row.data_resumo === dataResumo) || [];
  }

  getEventosIrregulares(rowData: any, dataResumo: string): any[] {
    return rowData.eventos?.filter((row: any) =>
      row.data_resumo === dataResumo && row.situaca_evento !== 0
    ) || [];
  }

  getMonitoramentosNaData(rowData: any, dataResumo: string): any[] {
    return rowData.monitoramento?.filter((mon: any) => mon.dataMonitoramento === dataResumo) || [];
  }

  private readonly CATEGORIAS_MONITORAMENTO = [
    { key: 'integracao_coleta', tipos: [7, 8], label: 'Monitoramento Integração/Coleta' },
    { key: 'bateria_painel', tipos: [1, 2], label: 'Monitoramento Bateria/Painel' },
    { key: 'nivel', tipos: [3, 4, 5], label: 'Monitoramento Nível' },
    { key: 'chuva', tipos: [6, 9], label: 'Monitoramento Chuva' },
  ];

  getMonitoramentosIconesNaData(rowData: any, dataResumo: string): any[] {
    const monitoramentos = this.getMonitoramentosNaData(rowData, dataResumo);
    const categorias = new Map<string, any>();

    monitoramentos.forEach((mon: any) => {
      const cat = this.CATEGORIAS_MONITORAMENTO.find(c => c.tipos.includes(Number(mon.tipo_evento_id)));
      if (!cat || categorias.has(cat.key)) {
        return;
      }
      categorias.set(cat.key, {
        key: cat.key,
        label: cat.label,
        cor: this.determinarCorMonitoramento(mon.tipo_evento_id),
      });
    });

    return Array.from(categorias.values());
  }

  getDescricaoTipoEvento(tipoEventoId: number): string {
    const tipos: { [key: number]: string } = {
      1: 'Bateria Baixa',
      2: 'Painel Baixa',
      3: 'Nível Travado',
      4: 'Nível Oscilante',
      5: 'Nível Fora Limites',
      6: 'Chuva',
      7: 'Coleta',
      8: 'Integração'
    };
    return tipos[tipoEventoId] || 'Desconhecido';
  }

  getDescricaoMonitoramento(mon: any): string {
    if (mon.tipo_evento?.descricao) {
      return mon.tipo_evento.descricao;
    }
    if (mon.tipo_evento_id) {
      return this.getDescricaoTipoEvento(mon.tipo_evento_id);
    }
    if (mon.tipoEventoId) {
      return this.getDescricaoTipoEvento(mon.tipoEventoId);
    }
    return 'Monitoramento';
  }

  getEventoColeta(rowData: any, dataResumo: string): any {
    return rowData.eventos?.find((evento: any) =>
      evento.tipo_evento_id === 7 && evento.data_resumo === dataResumo
    );
  }

  voltar(): void {
    // Passa os filtros de volta para preservar no componente de resumo
    this.router.navigate(['/monitoramento-estacoes'], {
      state: {
        rangeDate: this.rangeDate,
        estacao_id: this.navigationState?.estacao_id,
        grupo: this.navigationState?.grupo,
        cliente: this.navigationState?.cliente,
        sensor: this.navigationState?.sensor,
        plu_filtro: this.navigationState?.plu_filtro,
        flu_filtro: this.navigationState?.flu_filtro,
        integrador_filtro: this.navigationState?.integrador_filtro,
        coletor_filtro: this.navigationState?.coletor_filtro
      }
    });
  }

  exportarPDF(): void {
    if (!this.data || this.data.length === 0) {
      alert('Nenhum dado disponível para exportar.');
      return;
    }

    import('jspdf').then(jsPDF => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF.default('portrait', 'mm', 'a4');
        
        let startY = 20;
        const pageHeight = 277; // Altura útil da página A4 vertical em mm (297 - 20 de margem)
        const marginLeft = 14;
        const marginRight = 14;
        const pageWidth = 210; // Largura A4 vertical em mm
        const maxTextWidth = pageWidth - marginLeft - marginRight;

        // Cabeçalho
        doc.setFontSize(16);
        doc.text('MITSAT - Sistema de Monitoramento', marginLeft, startY);
        startY += 7;

        doc.setFontSize(14);
        doc.text('Relatório de Resumo de Eventos', marginLeft, startY);
        startY += 8;

        doc.setFontSize(10);
        doc.text(`Gerado em: ${this.dataGeracao}`, marginLeft, startY);
        startY += 5;
        doc.text(`Período: ${this.periodoFormatado}`, marginLeft, startY);
        startY += 5;
        
        // Quebrar linha no texto de Filtros
        const filtrosTexto = `Filtros: ${this.getDescricaoFiltros()}`;
        const filtrosLinhas = doc.splitTextToSize(filtrosTexto, maxTextWidth);
        doc.text(filtrosLinhas, marginLeft, startY);
        startY += (filtrosLinhas.length * 5);
        
        doc.text(`Total de Estações: ${this.data.length}`, marginLeft, startY);
        startY += 8;

        // Para cada estação
        this.data.forEach((estacao, estacaoIndex) => {
          // Verificar se precisa de nova página
          if (startY > pageHeight - 40) {
            doc.addPage();
            startY = 20;
          }

          // Título da estação - alinhado com a tabela
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.text(estacao.descricao, marginLeft, startY);
          doc.setFont(undefined, 'normal');
          startY += 8;

          // Preparar dados da tabela
          const tableData: any[] = [];
          
          this.columns.forEach(col => {
            const dataResumo = col.data_resumo;
            const eventosIrregulares = this.getEventosIrregulares(estacao, dataResumo);
            const monitoramentos = this.getMonitoramentosNaData(estacao, dataResumo);
            const eventoColeta = this.getEventoColeta(estacao, dataResumo);

            const eventosTexto = eventosIrregulares.length > 0 
              ? eventosIrregulares.map((e: any) => e.tipo_evento?.descricao || 'Evento').join(', ')
              : '-';

            const monitoramentosTexto = monitoramentos.length > 0
              ? monitoramentos.map((m: any) => this.getDescricaoMonitoramento(m)).join(', ')
              : '-';

            const disponibilidade = eventoColeta && eventoColeta.disponibilidade && eventoColeta.disponibilidade !== '-' && eventoColeta.disponibilidade !== 'N/A'
              ? eventoColeta.disponibilidade
              : '-';

            const atraso = eventoColeta && eventoColeta.atraso_medio_integracao && eventoColeta.atraso_medio_integracao !== '-' && eventoColeta.atraso_medio_integracao !== 'N/A'
              ? eventoColeta.atraso_medio_integracao
              : '-';

            const integracao = eventoColeta && eventoColeta.percentual_integracao && eventoColeta.percentual_integracao !== '-' && eventoColeta.percentual_integracao !== 'N/A'
              ? eventoColeta.percentual_integracao
              : '-';

            tableData.push([
              `${col.diaSemana}\n${col.dataFormatada}`,
              eventosTexto,
              monitoramentosTexto,
              disponibilidade,
              atraso,
              integracao
            ]);
          });

          // Criar tabela
          (doc as any).autoTable({
            head: [['Data', 'Eventos', 'Monitoramentos', 'Disp.', 'Atraso', 'Integração']],
            body: tableData,
            startY: startY,
            margin: { left: marginLeft, right: marginRight },
            styles: { 
              fontSize: 7,
              cellPadding: 2,
              overflow: 'linebreak',
              cellWidth: 'wrap'
            },
            headStyles: {
              fillColor: [51, 51, 51],
              textColor: [255, 255, 255],
              fontStyle: 'bold'
            },
            columnStyles: {
              0: { cellWidth: 30 },
              1: { cellWidth: 45 },
              2: { cellWidth: 45 },
              3: { cellWidth: 20 },
              4: { cellWidth: 25 },
              5: { cellWidth: 25 }
            },
            didDrawPage: (data: any) => {
              // Adicionar rodapé com informações e número da página
              const pageHeight = doc.internal.pageSize.getHeight();
              const pageWidth = doc.internal.pageSize.getWidth();
              const pageNumber = data.pageNumber;
              const totalPages = doc.internal.pages.length - 1;
              
              doc.setFontSize(8);
              doc.setTextColor(0, 0, 0);
              
              // Texto do rodapé (esquerda)
              const rodapeTexto = `MITSAT - Sistema de Monitoramento | ${this.dataGeracao}`;
              doc.text(rodapeTexto, marginLeft, pageHeight - 10);
              
              // Número da página (centro)
              doc.text(
                `Página ${pageNumber} de ${totalPages}`,
                pageWidth / 2,
                pageHeight - 10,
                { align: 'center' }
              );
            }
          });

          startY = (doc as any).lastAutoTable.finalY + 10;

          // Adicionar quebra de página se configurado
          if (this.quebraPaginaPorEstacao && estacaoIndex < this.data.length - 1) {
            if (startY < pageHeight) {
              doc.addPage();
              startY = 20;
            }
          }
        });

        // Salvar PDF
        const fileName = `Relatorio_Resumo_Eventos_${moment().format('YYYYMMDD_HHmmss')}.pdf`;
        doc.save(fileName);
      });
    });
  }

  getDescricaoFiltros(): string {
    const partes: string[] = [];

    if (this.filtrosAplicados.estacoes?.length > 0) {
      partes.push(`Estações: ${this.filtrosAplicados.estacoes.map(e => e.label).join(', ')}`);
    }
    if (this.filtrosAplicados.grupos?.length > 0) {
      partes.push(`Grupos: ${this.filtrosAplicados.grupos.map(g => g.label).join(', ')}`);
    }
    if (this.filtrosAplicados.clientes?.length > 0) {
      partes.push(`Clientes: ${this.filtrosAplicados.clientes.map(c => c.label).join(', ')}`);
    }

    return partes.length > 0 ? partes.join(' | ') : 'Todos os registros';
  }

  exportarExcel(): void {
    if (!this.data || this.data.length === 0) {
      alert('Nenhum dado disponível para exportar.');
      return;
    }

    const dadosExport = this.prepararDadosParaExportacao();
    
    import('xlsx').then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(dadosExport);
      const workbook = { Sheets: { 'Relatório Resumo Eventos': worksheet }, SheetNames: ['Relatório Resumo Eventos'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, 'Relatorio_Resumo_Eventos');
    });
  }

  prepararDadosParaExportacao(): any[] {
    const dadosExport: any[] = [];

    // Para cada estação
    this.data.forEach(estacao => {
      // Para cada data no período
      this.columns.forEach(col => {
        const dataResumo = col.data_resumo;
        const dataFormatada = col.dataFormatada;
        
        const linha: any = {
          'Nome da Estação': estacao.descricao,
          'Data': dataFormatada
        };

        // Adicionar colunas para cada evento cadastrado
        // Usa o mesmo critério do relatório: apenas eventos irregulares (situaca_evento !== 0)
        this.eventosCadastrados.forEach(evento => {
          const temEvento = estacao.eventos?.some((e: any) => 
            e.tipo_evento_id === evento.id && 
            e.data_resumo === dataResumo && 
            e.situaca_evento !== 0
          );
          linha[evento.descricao_evento || `Evento ${evento.id}`] = temEvento ? 'X' : '';
        });

        // Monitoramento
        const temMonitoramento = this.hasMonitoramentosNaData(estacao, dataResumo);
        linha['Monitoramento'] = temMonitoramento ? 'X' : '';

        // Disponibilidade (vem do evento tipo 7 - Coleta)
        const eventoColeta = estacao.eventos?.find((e: any) => 
          e.tipo_evento_id === 7 && e.data_resumo === dataResumo
        );
        linha['Disponibilidade'] = (eventoColeta && eventoColeta.disponibilidade && eventoColeta.disponibilidade !== '-' && eventoColeta.disponibilidade !== 'N/A') ? eventoColeta.disponibilidade : '';

        // Atraso (vem do evento tipo 7 - Coleta)
        linha['Atraso'] = (eventoColeta && eventoColeta.atraso_medio_integracao && eventoColeta.atraso_medio_integracao !== '-' && eventoColeta.atraso_medio_integracao !== 'N/A') ? 'X' : '';

        // Integração (vem do evento tipo 7 - Coleta)
        linha['Integração'] = (eventoColeta && eventoColeta.percentual_integracao && eventoColeta.percentual_integracao !== '-' && eventoColeta.percentual_integracao !== 'N/A') ? 'X' : '';

        dadosExport.push(linha);
      });
    });

    return dadosExport;
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import('file-saver').then(FileSaver => {
      const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const EXCEL_EXTENSION = '.xlsx';
      const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
      FileSaver.default.saveAs(data, fileName + '_' + moment().format('YYYYMMDD_HHmmss') + EXCEL_EXTENSION);
    });
  }
}
